import { workCover } from "./workDetails";
import { WorkDetailsFields } from "./WorkDetailsFields";
import { firstPicture } from "./pageThumbnails";
import { WorkThumbnail } from "./WorkThumbnail";
import "./PagesMenu.css";
import { isWork, pageMeta, workDate } from "../../pages/pageMetadata";
import { canMovePage } from "../../pages/pageOrder";
import { WorkLanguage, workItemName } from "../workLanguage";
import { useContext, useState } from "react";
import {
  MoreHorizontal,
  FileText,
  Home,
  Search,
  Check,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Pencil,
  ImagePlus,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import type { EditorAsset, EditorBlock, EditorPage } from "../editor.types";
import type { PageUpdateValues } from "../hooks/useSiteEditor";
import { BottomSheet } from "./BottomSheet";
import { MenuItem, MenuSection, MenuSeparator } from "./Menu";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldError,
  Hint,
  IconButton,
  Input,
  Notice,
  SectionHeading,
  Select,
} from "../../../components/ui";

type PagesMenuProps = {
  open: boolean;
  inline?: boolean;
  pages: EditorPage[];
  documents?: Record<string, EditorBlock[]>;
  assetsById?: Map<string, EditorAsset>;
  labels: { plural: string; singular: string };
  trash: EditorPage[];
  showOnHome: (page: EditorPage) => boolean;
  onShowOnHome: (page: EditorPage, show: boolean) => Promise<string | null>;
  onRestorePage: (page: EditorPage) => Promise<string | null>;
  onRemoveSamples: () => Promise<string | null>;
  currentPageId: string;
  onClose: () => void;
  onSelectPage: (pageId: string) => void;
  onAddLibraryItem: (pageId: string, libraryId: string) => void;
  onCreatePage: (title: string, work?: boolean) => Promise<string | null>;
  onUpdatePage: (
    pageId: string,
    values: PageUpdateValues,
  ) => Promise<string | null>;
  onDeletePage: (pageId: string) => Promise<string | null>;
  onDuplicatePage: (page: EditorPage) => Promise<string | null>;
  onMovePage: (pageId: string, delta: -1 | 1) => Promise<string | null>;
  onSetHomePage: (pageId: string) => Promise<string | null>;
  onUploadCover?: (file: File) => Promise<EditorAsset>;
};

const pageListClass =
  "overflow-hidden rounded-2xl border border-hazumu-border bg-hazumu-warm-white empty:hidden [&>div+div]:border-t [&>div+div]:border-hazumu-border";

/** The two kinds offered when adding a page; the chosen one is highlighted. */
const pageKindOptionClass =
  "flex cursor-pointer gap-3 rounded-xl border border-hazumu-border p-4 transition-colors has-checked:border-hazumu-terracotta has-checked:bg-hazumu-blush";

const SectionCount = ({ count }: { count: number }) => (
  <span className="text-xs font-normal text-hazumu-muted">{count}</span>
);

export const PagesMenu = ({
  open,
  inline = false,
  pages,
  documents = {},
  assetsById,
  currentPageId,
  showOnHome,
  onShowOnHome,
  labels,
  trash,
  onRestorePage,
  onRemoveSamples,
  onClose,
  onSelectPage,
  onCreatePage,
  onUpdatePage,
  onDeletePage,
  onDuplicatePage,
  onMovePage,
  onSetHomePage,
  onUploadCover,
}: PagesMenuProps) => {
  const [creating, setCreating] = useState<boolean | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [optionsId, setOptionsId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(5);
  const [order, setOrder] = useState("newest");
  const [confirmSamples, setConfirmSamples] = useState(false);
  if (!open) return null;
  const optionsPage = pages.find((p) => p.id === optionsId);
  const hasWork = pages.some(isWork);
  const work = pages
    .filter(isWork)
    .filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) =>
      order === "name"
        ? a.title.localeCompare(b.title)
        : order === "oldest"
          ? workDate(a).localeCompare(workDate(b))
          : workDate(b).localeCompare(workDate(a)),
    );
  const openOptions = (page: EditorPage) => {
    setOptionsId(page.id);
    setEditingId(null);
    setDeleting(false);
    setError("");
  };
  const row = (page: EditorPage) => {
    const workItem = isWork(page);
    const picture = workItem ? workCover(page, firstPicture(documents[page.id])) : null;
    const date = workDate(page);
    const parsed = date ? new Date(`${date}T00:00:00Z`) : null;
    const dateLabel =
      parsed && !Number.isNaN(parsed.getTime())
        ? new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
          }).format(parsed)
        : "No date";
    return (
      <div className="flex min-w-0 items-center gap-1 pr-2 pl-3 transition-colors duration-120 hover:bg-hazumu-canvas data-[current=true]:bg-hazumu-blush [&_button:focus-visible]:rounded-lg [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-hazumu-terracotta [&_button:focus-visible]:-outline-offset-2" data-current={page.id === currentPageId}>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left"
          aria-current={page.id === currentPageId ? "page" : undefined}
          onClick={() => onSelectPage(page.id)}
        >
          {workItem ? (
            <WorkThumbnail
              picture={picture}
              asset={
                picture?.asset_id
                  ? assetsById?.get(picture.asset_id)
                  : undefined
              }
            />
          ) : (
            <span className="grid w-7 flex-none place-items-center text-hazumu-muted">
              {page.is_home ? <Home size={18} /> : <FileText size={18} />}
            </span>
          )}
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-sm leading-[1.4] font-medium wrap-anywhere">{page.title}</span>
            {workItem ? (
              <span className="flex flex-wrap items-center gap-2 text-[11px] leading-[1.4] text-hazumu-muted">
                {dateLabel === "No date" ? (
                  <span>No date</span>
                ) : (
                  <time dateTime={date}>{dateLabel}</time>
                )}
                {pageMeta(page).sample === true && (
                  <Badge>Sample</Badge>
                )}
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-2 text-[11px] leading-[1.4] text-hazumu-muted">
                {page.is_home
                  ? "Homepage"
                  : page.is_visible
                    ? "In site menu"
                    : "Not in menu"}
              </span>
            )}
          </span>
        </button>
        <button
          type="button"
          aria-label={`Options for ${page.title}`}
          className="grid h-11 w-9 flex-none place-items-center rounded-lg text-hazumu-muted hover:bg-hazumu-border hover:text-hazumu-ink"
          onClick={() => openOptions(page)}
        >
          <MoreHorizontal size={20} />
        </button>
      </div>
    );
  };

  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setError("");
    const message = await action();
    setBusy(false);
    if (message) setError(message);
    return !message;
  };

  return (
    <BottomSheet
      inline={inline}
      disabled={busy}
      onClose={onClose}
      open={open}
      title="Pages"
      titleId="pages-menu-title"
    >
      <div className="flex min-w-0 flex-col gap-7">
        {error && <Notice className="shrink-0">{error}</Notice>}

        <section className="min-w-0">
          <SectionHeading
            action={
              <Button
                disabled={busy}
                onClick={() => {
                  setNewTitle("");
                  setCreating(false);
                }}
                size="sm"
                variant="ghost"
              >
                <Plus size={14} />
                Add page
              </Button>
            }
          >
            Your pages{" "}
            <SectionCount count={pages.filter((p) => !isWork(p)).length} />
          </SectionHeading>
          <div className={pageListClass}>
            {pages
              .filter((p) => !isWork(p))
              .map((p) => (
                <div key={p.id}>{row(p)}</div>
              ))}
          </div>
        </section>
        <section className="min-w-0">
          <SectionHeading
            action={
              <Button
                disabled={busy}
                onClick={() => {
                  setNewTitle("");
                  setCreating(true);
                }}
                size="sm"
                variant="ghost"
              >
                <Plus size={14} />
                Add {labels.singular}
              </Button>
            }
          >
            Your {labels.plural.toLowerCase()}{" "}
            <SectionCount count={pages.filter(isWork).length} />
          </SectionHeading>
          <Hint className="-mt-2 mb-3">Create once. Showcase anywhere.</Hint>
          {hasWork && (
          <div className="mb-3 flex gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                size={16}
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-hazumu-muted"
              />
              <Input
                className="pl-9"
                aria-label="Search work"
                placeholder="Search…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setLimit(20);
                }}
              />
            </div>
            <Select
              aria-label="Sort work"
              className="max-w-36 flex-none"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A–Z</option>
            </Select>
          </div>
          )}
          <div className={pageListClass}>
            {work.slice(0, limit).map((p) => (
              <div key={p.id}>{row(p)}</div>
            ))}
          </div>
          {!hasWork ? (
            <EmptyState
              icon={<ImagePlus size={20} />}
              title={`Your first ${labels.singular}`}
              action={
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setNewTitle("");
                    setCreating(true);
                  }}
                >
                  <Plus size={14} />
                  Add {labels.singular}
                </Button>
              }
            >
              Add it once here, then show it on any page with a work gallery.
            </EmptyState>
          ) : (
            !work.length && <Hint className="py-4">No matching {labels.plural.toLowerCase()}.</Hint>
          )}
          <div className="mt-2 -ml-3 flex flex-wrap gap-1 empty:hidden">
            {work.length > limit && (
              <Button
                onClick={() => setLimit((n) => n + 20)}
                size="sm"
                variant="ghost"
              >
                Show more ({work.length - limit})
              </Button>
            )}
            {pages.some((p) => pageMeta(p).sample === true) && (
              <Button
                disabled={busy}
                onClick={() => setConfirmSamples(true)}
                size="sm"
                variant="ghost"
              >
                Remove sample work
              </Button>
            )}
          </div>
        </section>
      </div>
      {creating !== null && (
        <BottomSheet
          open
          disabled={busy}
          title="Add a page"
          titleId="create-page-title"
          onClose={() => setCreating(null)}
        >
          <div className="mb-5 space-y-3">
            <label className={pageKindOptionClass}>
              <input
                className="accent-hazumu-terracotta"
                type="radio"
                name="new-page-kind"
                checked={!creating}
                onChange={() => setCreating(false)}
              />
              <span>
                <strong>Website page</strong>
                <span className="block text-sm text-hazumu-muted">
                  Home, About, Contact or My Projects. Add a work gallery to
                  showcase your projects here.
                </span>
              </span>
            </label>
            <label className={pageKindOptionClass}>
              <input
                className="accent-hazumu-terracotta"
                type="radio"
                name="new-page-kind"
                checked={creating}
                onChange={() => setCreating(true)}
              />
              <span>
                <strong>{workItemName(labels)}</strong>
                <span className="block text-sm text-hazumu-muted">
                  A project, photo story, app or video. Add it once and showcase
                  it anywhere.
                </span>
              </span>
            </label>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(() =>
                onCreatePage(newTitle.trim() || "Untitled", creating),
              ).then((ok) => {
                if (ok) setCreating(null);
              });
            }}
          >
            <Field label="Page name">
              <Input
                autoFocus
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
                placeholder={creating ? "e.g. Cosy Notes" : "e.g. My Projects"}
              />
            </Field>
            <Button className="mt-4" disabled={busy} type="submit" variant="primary">
              Create {creating ? labels.singular : "website page"}
            </Button>
          </form>
        </BottomSheet>
      )}
      {confirmSamples && (
        <BottomSheet
          open
          disabled={busy}
          title="Remove sample work?"
          titleId="remove-samples-title"
          onClose={() => setConfirmSamples(false)}
        >
          <p className="mb-4 text-sm text-hazumu-muted">
            This removes the examples still marked as samples, including any
            edits. You can restore them from Recently deleted.
          </p>
          <Button
            disabled={busy}
            onClick={() =>
              void run(onRemoveSamples).then((ok) => {
                if (ok) setConfirmSamples(false);
              })
            }
            variant="danger"
          >
            Remove samples
          </Button>
        </BottomSheet>
      )}
      {trash.length > 0 && (
        <details className="border-t border-hazumu-border pt-4 text-xs text-hazumu-muted [&>summary]:cursor-pointer [&>summary]:py-2">
          <summary>Recently deleted ({trash.length})</summary>
          <Hint>Restore a page and its content.</Hint>
          {trash.map((p) => (
            <div className="flex items-center gap-2 py-1.5" key={p.id}>
              <span className="min-w-0 flex-1 text-sm text-hazumu-ink wrap-anywhere">
                {p.title}
              </span>
              <Button
                disabled={busy}
                onClick={() => void run(() => onRestorePage(p))}
                size="sm"
                variant="secondary"
              >
                Restore
              </Button>
            </div>
          ))}
        </details>
      )}
      {optionsPage && (
        <BottomSheet
          open
          onClose={() => !busy && setOptionsId(null)}
          disabled={busy}
          title={deleting ? `Delete “${optionsPage.title}”?` : optionsPage.title}
          titleId="page-options-title"
        >
          {error && <FieldError>{error}</FieldError>}
          {deleting ? (
            <>
              <p className="mb-4 text-sm text-hazumu-muted">
                {isWork(optionsPage)
                  ? "It disappears from every gallery it’s in."
                  : "Work shown on this page is kept."}{" "}
                You can restore it from Recently deleted.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={busy}
                  variant="danger"
                  onClick={() =>
                    void run(() => onDeletePage(optionsPage.id)).then((ok) => {
                      if (ok) setOptionsId(null);
                    })
                  }
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Delete
                </Button>
                <Button
                  disabled={busy}
                  variant="ghost"
                  onClick={() => setDeleting(false)}
                >
                  Keep
                </Button>
              </div>
            </>
          ) : editingId === optionsPage.id ? (
            <PageEditForm
              page={optionsPage}
              blocks={documents[optionsPage.id] ?? []}
              onUploadCover={onUploadCover}
              assetsById={assetsById}
              busy={busy}
              onCancel={() => setEditingId(null)}
              onDelete={async () => {
                if (await run(() => onDeletePage(optionsPage.id)))
                  setOptionsId(null);
              }}
              onDuplicate={async () => {
                if (await run(() => onDuplicatePage(optionsPage)))
                  setOptionsId(null);
              }}
              onSave={async (values) => {
                if (await run(() => onUpdatePage(optionsPage.id, values)))
                  setOptionsId(null);
              }}
            />
          ) : (
            <div className="group/menu -mx-2" data-sheet="true">
              <MenuSection>
                {isWork(optionsPage) && (
                  <MenuItem
                    icon={<Eye size={16} />}
                    trailing={showOnHome(optionsPage) ? "On" : "Off"}
                    disabled={busy}
                    onClick={() =>
                      void run(() =>
                        onShowOnHome(optionsPage, !showOnHome(optionsPage)),
                      )
                    }
                  >
                    Show on Home
                  </MenuItem>
                )}
                <MenuItem
                  icon={optionsPage.is_visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  trailing={optionsPage.is_visible ? "On" : "Off"}
                  disabled={busy || optionsPage.is_home || isWork(optionsPage)}
                  onClick={() =>
                    void run(() =>
                      onUpdatePage(optionsPage.id, {
                        is_visible: !optionsPage.is_visible,
                      }),
                    )
                  }
                >
                  Show in menu
                </MenuItem>
                <MenuItem
                  icon={<Star size={16} />}
                  disabled={busy || optionsPage.is_home || !optionsPage.is_visible}
                  onClick={() => void run(() => onSetHomePage(optionsPage.id))}
                >
                  {optionsPage.is_home ? "Homepage" : "Make homepage"}
                </MenuItem>
                <MenuItem
                  icon={<Pencil size={16} />}
                  disabled={busy}
                  onClick={() => setEditingId(optionsPage.id)}
                >
                  Page details
                </MenuItem>
              </MenuSection>
              <MenuSeparator />
              <MenuSection>
                {/* Work is listed by date or name, so a manual move wouldn't show. */}
                {!isWork(optionsPage) && (
                <>
                <MenuItem
                  icon={<ChevronUp size={16} />}
                  disabled={busy || !canMovePage(pages, optionsPage.id, -1)}
                  onClick={() => void run(() => onMovePage(optionsPage.id, -1))}
                >
                  Move up
                </MenuItem>
                <MenuItem
                  icon={<ChevronDown size={16} />}
                  disabled={busy || !canMovePage(pages, optionsPage.id, 1)}
                  onClick={() => void run(() => onMovePage(optionsPage.id, 1))}
                >
                  Move down
                </MenuItem>
                </>
                )}
                <MenuItem
                  icon={<Copy size={16} />}
                  disabled={busy}
                  onClick={() =>
                    void run(() => onDuplicatePage(optionsPage)).then((ok) => {
                      if (ok) setOptionsId(null);
                    })
                  }
                >
                  Duplicate
                </MenuItem>
              </MenuSection>
              <MenuSeparator />
              <MenuItem
                icon={<Trash2 size={16} />}
                tone="danger"
                disabled={busy || optionsPage.is_home}
                onClick={() => setDeleting(true)}
              >
                Delete…
              </MenuItem>
            </div>
          )}
        </BottomSheet>
      )}
    </BottomSheet>
  );
};

type PageEditFormProps = {
  blocks?: EditorBlock[];
  /** Uploads a cover picture for a work item. */
  onUploadCover?: (file: File) => Promise<EditorAsset>;
  assetsById?: Map<string, EditorAsset>;
  page: EditorPage;
  busy: boolean;
  onSave: (values: PageUpdateValues) => Promise<void>;
  onCancel: () => void;
  onDuplicate: () => Promise<void>;
  onDelete: () => Promise<void>;
};

export const PageEditForm = ({
  blocks = [],
  onUploadCover,
  assetsById,
  page,
  busy,
  onSave,
  onCancel,
  onDuplicate,
  onDelete,
}: PageEditFormProps) => {
  const labels = useContext(WorkLanguage);
  const [workKind, setWorkKind] = useState(isWork(page));
  const [details, setDetails] = useState(pageMeta(page));
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="-mx-5 -mb-5 flex min-h-0 flex-col sm:-mx-6 sm:-mb-6">
      <div className="min-h-0 space-y-4 overflow-y-auto px-5 pb-4 sm:px-6">
        {workKind && <WorkDetailsFields page={{...page, title, seo: details}} blocks={blocks} assetsById={assetsById} disabled={busy} onChange={setDetails} onUploadCover={onUploadCover} />}
        <details open={!workKind || undefined} className="group/settings">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 py-1 text-sm font-medium text-hazumu-ink [&::-webkit-details-marker]:hidden">
            <ChevronRight size={16} aria-hidden="true" className="text-hazumu-muted transition-transform group-open/settings:rotate-90" />
            Page settings
            {workKind && <span className="font-normal text-hazumu-muted">· title, address, type</span>}
          </summary>
          <div className="mt-3 space-y-3">
            {!page.is_home && (
              <Field
                label="Page type"
                hint={
                  workKind
                    ? "One piece of work you can showcase anywhere. Kept in Your work, outside the site menu."
                    : "A page such as About or My Projects. It can contain a gallery of your work."
                }
              >
                <Select
                  value={workKind ? "work" : "website"}
                  disabled={busy}
                  onChange={(event) => setWorkKind(event.target.value === "work")}
                >
                  <option value="website">Website page</option>
                  <option value="work">{workItemName(labels)}</option>
                </Select>
              </Field>
            )}
            <Field label="Title">
              <Input
                onChange={(event) => setTitle(event.target.value)}
                type="text"
                value={title}
              />
            </Field>
            <Field label="Address">
              <Input
                onChange={(event) => setSlug(event.target.value)}
                type="text"
                value={slug}
              />
            </Field>
          </div>
        </details>
      </div>
      {/* Pinned below the scrolling fields, so the actions never scroll away. */}
      <div className="shrink-0 border-t border-hazumu-border px-5 py-3 sm:px-6">
        {confirmingDelete ? (
          <div className="space-y-2">
            <p className="text-sm text-hazumu-ink">
              Delete “{page.title}”? You can restore it from Recently deleted.
              {workKind ? "" : " Work shown on this page is kept."}
            </p>
            <div className="flex gap-2">
              <Button
                disabled={busy}
                onClick={() => void onDelete()}
                size="sm"
                variant="danger"
              >
                <Trash2 size={14} />
                Delete
              </Button>
              <Button
                disabled={busy}
                onClick={() => setConfirmingDelete(false)}
                size="sm"
                variant="ghost"
              >
                Keep
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              disabled={busy || !title.trim()}
              onClick={() =>
                void onSave({
                  title,
                  slug,
                  seo: { ...details, work: workKind },
                  ...(workKind !== isWork(page) ? { is_visible: !workKind } : {}),
                })
              }
              variant="primary"
            >
              <Check size={16} />
              Save
            </Button>
            <Button disabled={busy} onClick={onCancel} variant="ghost">
              Cancel
            </Button>
            <IconButton
              aria-label="Duplicate"
              title="Duplicate"
              className="ml-auto"
              disabled={busy}
              onClick={() => void onDuplicate()}
            >
              <Copy size={17} />
            </IconButton>
            <IconButton
              aria-label="Delete…"
              title="Delete"
              disabled={busy || page.is_home}
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 size={17} />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
};
